import {useMutation, useQueries, useQuery, useQueryClient} from '@tanstack/react-query';
import {apiRequest} from '../../utils/AxiosHelper';
import API from '../../modules/settings/APIs';
import {API_PARAMS} from '../../constants/constants';
import {useAppSelector} from '../hooks';

export interface AssessmentOption {
  id: number;
  label: string;
  numericValue: number;
  variableId?: number;
  idVariable?: number;
  groupId?: number;
  idVariableGroup?: number;
  scaleBandId?: number | null;
  scaleBandLabel?: string | null;
  scaleBandColor?: string | null;
  unitOfMeasure?: string | null;
  formula?: string | null;
  state?: string;
}

export interface AssessmentVariable {
  id: number;
  idVariable?: number;
  groupId?: number;
  idVariableGroup?: number;
  name?: string;
  description?: string;
  descriptionGroup?: string;
  factor?: number | null;
  unitOfMeasure?: string | null;
  state?: string;
  // Selected option metadata returned by /assessment/issue:
  idValue?: number | null;
  equivalent?: number | null;
  equivalentValue?: number | null;
  descriptionSelected?: string | null;
}

export interface AssessmentVariableGroup {
  id: number;
  idVariableGroup?: number;
  type?: string;
  name?: string;
  description?: string;
  descriptionGroup?: string;
  abbreviation?: string;
  weightPct?: number | null;
  unitOfMeasure?: string | null;
  configVersion?: number | null;
  state?: string;
  children?: AssessmentVariable[];
}

export interface SaveIssueAssessmentPayload {
  version: {
    issueId: number;
    uid: string | null,
    configGroupId?: number | null;
    configVersion?: number | null;
    finalScore: number;
    scaleBandId?: number | null;
    status?: string | null;
  };
  groupScores: Array<{
    groupId: number;
    groupScore: number;
    weightedScore: number;
  }>;
  details: Array<{
    groupId: number;
    variableId: number;
    optionId: number;
    numericValue: number;
    variableLabelSnap?: string;
    optionLabelSnap?: string;
    factorSnap?: number | null;
    weightSnap?: number | null;
  }>;
}

const issueAssessmentKeys = {
  variables: (issueId?: string | number, type?: string, state?: string) =>
    ['issue-assessment', 'variables', issueId, type, state] as const,
  options: (variableId: number | string) =>
    ['impact-assessment', 'values', {idVariable: variableId}] as const,
  groupOptions: (groupId: number | string) =>
    ['impact-assessment', 'values', {idVariableGroup: groupId}] as const,
};

export const useIssueAssessmentVariables = (issueId?: string | number) => {
  const {isAuthenticated, token} = useAppSelector((state) => state.session);
  const numericIssueId = issueId != null && issueId !== 'new' ? Number(issueId) : NaN;
  const enabled = Number.isFinite(numericIssueId) && isAuthenticated && !!token;

  return useQuery<AssessmentVariable[]>({
    queryKey: issueAssessmentKeys.variables(numericIssueId, API_PARAMS.IMPACT.ISSUES_ENTITY, API_PARAMS.IS_ACTIVE),
    queryFn: async () => {
      const response = await apiRequest.get(API.issues.assessment.issue, {
        params: {
          type: API_PARAMS.IMPACT.ISSUES_ENTITY,
          state: API_PARAMS.IS_ACTIVE,
          idIssue: numericIssueId,
        },
      });
      return (response.data?.data ?? []) as AssessmentVariable[];
    },
    staleTime: 60 * 1000,
    enabled,
  });
};

/**
 * Fetch the option list for each variable. By default every variable's options
 * are fetched up-front, but passing `activeIds` switches to lazy mode: only the
 * variables in the set are fetched (e.g. those already selected or whose Select
 * the user has opened). Other queries stay disabled until their id is added.
 */
export const useVariableOptionsBatch = (
  variableIds: Array<number | string>,
  activeIds?: Set<number>
) => {
  const {isAuthenticated, token} = useAppSelector((state) => state.session);
  const baseEnabled = isAuthenticated && !!token;

  return useQueries({
    queries: variableIds.map((id) => ({
      queryKey: issueAssessmentKeys.options(id),
      queryFn: async (): Promise<AssessmentOption[]> => {
        const response = await apiRequest.get(API.parameters.config.assessment.values, {
          params: {
            idVariable: id,
            state: API_PARAMS.IS_ACTIVE,
          },
        });
        return (response.data?.data ?? []) as AssessmentOption[];
      },
      staleTime: 5 * 60 * 1000,
      enabled: baseEnabled && id != null && (activeIds == null || activeIds.has(Number(id))),
    })),
  });
};

/**
 * Fetch the group-level options (the `variable_id IS NULL` rows) for each group.
 * These carry the per-group formulas used to resolve a group's level from the
 * array of selected variable scores.
 */
export const useGroupOptionsBatch = (groupIds: Array<number | string>) => {
  const {isAuthenticated, token} = useAppSelector((state) => state.session);
  const baseEnabled = isAuthenticated && !!token;

  return useQueries({
    queries: groupIds.map((id) => ({
      queryKey: issueAssessmentKeys.groupOptions(id),
      queryFn: async (): Promise<AssessmentOption[]> => {
        const response = await apiRequest.get(API.parameters.config.assessment.values, {
          params: {
            idVariableGroup: id,
            state: API_PARAMS.IS_ACTIVE,
          },
        });
        return (response.data?.data ?? []) as AssessmentOption[];
      },
      staleTime: 5 * 60 * 1000,
      enabled: baseEnabled && id != null,
    })),
  });
};

export const useSaveIssueAssessment = (issueId?: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: SaveIssueAssessmentPayload) => {
      const response = await apiRequest.post(API.issues.assessment.issue, payload);
      return response.data;
    },
    onSuccess: (data, payload) => {

      const numericIssueId = payload.version.issueId;
      const hasNumericId = Number.isFinite(numericIssueId);

      const saved = (data?.data ?? null) as AssessmentVariable[] | null;

      if (hasNumericId && Array.isArray(saved)) {
        queryClient.setQueryData(
          issueAssessmentKeys.variables(numericIssueId, API_PARAMS.IMPACT.ISSUES_ENTITY, API_PARAMS.IS_ACTIVE),
          saved
        );
      } else if (hasNumericId) {
        queryClient.invalidateQueries({queryKey: ['issue-assessment', 'variables', numericIssueId]});
      }

      if (issueId != null) {
        queryClient.invalidateQueries({queryKey: ['issues', 'detail', String(issueId)]});
      }
    },
  });
};
